import { useLanguage } from "./i18n";
import { useEffect, useRef, useState } from "react";
import { LockstepDriver, ViewerCore, type MovablePart, type ViewerSnapshot, type ViewerTelemetry } from "../viewer/core";
import { releaseAssetPackage } from "../viewer/assets";
import { candidateName, type Candidate, type Telemetry } from "./rank_api";
import InteractionGuide from "./InteractionGuide";

interface Props {
  candidates: Candidate[]; telemetry: Record<string, Telemetry>;
  onTelemetry: (token: string, value: ViewerTelemetry) => void;
  practice?: boolean;
  active?: boolean;
  /** Admin sessions show each candidate's method name next to its letter. */
  admin?: boolean;
  /** Submission thresholds, stated up front so the gate is not discovered at submit time.
   *  Absent in the organizer review app, which never submits and therefore has no gate. */
  unlock?: { min_drags_per_asset: number; min_active_ms_per_asset: number };
  /** What the selected dimension needs: part decomposition is judged on the coloured view,
   *  so selecting it opens that view instead of asking the participant to find the toggle. */
}
export default function SimulationPanel({ candidates, telemetry, onTelemetry, unlock, practice = false, active = true, admin = false }: Props) {
  const { t, tf } = useLanguage();
  const [selected, setSelected] = useState<string[]>([candidates[0]!.token]);
  const [partColors, setPartColors] = useState(false);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [slow, setSlow] = useState<Record<string, boolean>>({});
  const [movable, setMovable] = useState<Record<string, MovablePart[]>>({});
  const [highlighted, setHighlighted] = useState<Record<string, number | null>>({});
  const highlightedRef = useRef(highlighted); highlightedRef.current = highlighted;
  const canvases = useRef(new Map<string, HTMLCanvasElement>());
  const snapshots = useRef(new Map<string, ViewerSnapshot>());
  const cores = useRef(new Map<string, ViewerCore>());
  const callback = useRef(onTelemetry); callback.current = onTelemetry;
  const telemetryRef = useRef(telemetry); telemetryRef.current = telemetry;
  const colorRef = useRef(partColors); colorRef.current = partColors;
  const activeRef = useRef(active); activeRef.current = active;
  const driverRef = useRef<LockstepDriver | null>(null);
  const [hasOpened, setHasOpened] = useState(active);

  useEffect(() => {
    if (active) { setHasOpened(true); driverRef.current?.start(); }
    else {
      driverRef.current?.stop();
      for (const core of cores.current.values()) core.snapshot();
    }
  }, [active]);

  useEffect(() => {
    if (!hasOpened) return;
    let cancelled = false;
    let driver: LockstepDriver | null = null;
    const local = new Map<string, ViewerCore>();
    setStatus("loading"); setError("");
    void Promise.allSettled(selected.map(async (token) => {
      const candidate = candidates.find((item) => item.token === token)!;
      const canvas = canvases.current.get(token)!;
      const core = await ViewerCore.create({
        canvas, assetUrl: candidate.scene_url, appearanceUrl: candidate.appearance_url ?? null,
        ...(candidate.initial_camera_direction ? { initialCameraDirection: candidate.initial_camera_direction } : {}),
        onTelemetry: (value) => { if (!cancelled) {
          callback.current(token, value);
          const delayed = !value.paused && !value.faulted && value.fps > 0 && (value.simulationRtf ?? 1) < 0.8;
          setSlow((previous) => previous[token] === delayed ? previous : { ...previous, [token]: delayed });
        } },
      });
      return { token, core };
    })).then((results) => {
      for (const result of results) {
        if (result.status === "fulfilled") local.set(result.value.token, result.value.core);
      }
      const failed = results.find((result) => result.status === "rejected");
      if (cancelled || failed) {
        for (const core of local.values()) core.dispose();
        local.clear();
        if (!cancelled) { setStatus("error"); setError("仿真加载失败，请重试。"); }
        return;
      }
      for (const [token, core] of local) {
        const saved = snapshots.current.get(token);
        if (saved) core.restore(saved);
        else {
          const baseline = telemetryRef.current[token];
          if (baseline) core.restoreCounters({
            activeMs: baseline.active_ms, dragCount: baseline.drag_count,
            resetCount: baseline.reset_count, fps: baseline.fps, jointCoverage: 0,
            simulationFaultCount: baseline.simulation_fault_count,
          });
        }
        core.setPartColors(colorRef.current);
        core.highlightMovablePart(highlightedRef.current[token] ?? null);
      }
      setMovable((previous) => ({ ...previous, ...Object.fromEntries([...local].map(([token, core]) => [token, core.getMovableParts()])) }));
      cores.current = local;
      driver = new LockstepDriver([...local.values()]);
      driverRef.current = driver;
      if (activeRef.current) driver.start();
      setStatus("ready");
    }).catch(() => {
      for (const core of local.values()) core.dispose();
      local.clear();
      if (!cancelled) { setStatus("error"); setError("仿真状态恢复失败，请重试。"); }
    });
    return () => {
      cancelled = true; driver?.stop();
      if (driverRef.current === driver) driverRef.current = null;
      for (const [token, core] of local) {
        snapshots.current.set(token, core.snapshot());
        core.dispose();
      }
      local.clear(); cores.current = new Map();
    };
  }, [selected.join(","), retry, hasOpened]);

  useEffect(() => {
    for (const core of cores.current.values()) core.setPartColors(partColors);
  }, [partColors]);

  // Applied when the suggestion changes, not on every render, so a participant who toggles
  // the view by hand keeps their choice for as long as they stay on that dimension.

  useEffect(() => () => {
    for (const candidate of candidates) releaseAssetPackage(candidate.scene_url);
  }, [candidates]);

  function compare(token: string) {
    setSelected((current) => {
      if (current.includes(token)) return current.length > 1 ? current.filter((value) => value !== token) : current;
      return current.length < 2 ? [...current, token] : [current[0]!, token];
    });
  }
  return <section className="rs-simulation">
    <div className="rs-section-heading"><div><span className="rs-step">02</span><h2>{t("亲手操作")}</h2>
      {unlock && <p><strong>{tf("六个产物各需操作 {0} 次、查看 {1} 秒才能提交。",
        unlock.min_drags_per_asset, Math.round(unlock.min_active_ms_per_asset / 1000))}</strong></p>}</div>
    </div>
    <InteractionGuide practice={practice} />
    <div className="rs-model-tabs">{candidates.map((candidate) => <div key={candidate.token}>
      <button type="button" data-select-candidate={candidate.label} className={selected.includes(candidate.token) ? "selected" : ""}
        onClick={() => setSelected([candidate.token])}>{t("产物")} {candidateName(candidate, admin)}</button>
      <button type="button" className="rs-compare-button" aria-label={t("并排比较产物 ") + candidate.label}
        onClick={() => compare(candidate.token)}>⊕</button>
    </div>)}
      <button type="button" className="rs-pill-toggle rs-model-tabs-end" data-part-colors
        aria-pressed={partColors} onClick={() => setPartColors((value) => !value)}>{t("部件着色")}</button>
    </div>
    <div className={"rs-canvases count-" + selected.length} data-simulation-status={status}>
      {selected.map((token) => {
        const candidate = candidates.find((item) => item.token === token)!;
        return <div className="rs-canvas-panel" key={token} data-zoom-hint-text={t("按住 Ctrl（⌘）滚动以缩放")}>
          <div className="rs-canvas-label"><strong>{t("产物")} {candidateName(candidate, admin)}</strong>
            <button type="button" onClick={() => cores.current.get(token)?.reset(true)} disabled={status !== "ready"}>{t("重置并暂停")}</button></div>
          <canvas aria-label={t("操作产物 ") + candidate.label} data-candidate={candidate.label}
            ref={(element) => { if (element) canvases.current.set(token, element); else canvases.current.delete(token); }} />
          {status === "loading" && <div className="rs-canvas-overlay">{t("正在准备仿真…")}</div>}
          {(telemetry[token]?.simulation_fault_count ?? 0) > 0 && <p className="rs-fault">{t("仿真曾出现异常；可重置后继续尝试，按实际表现排名。")}</p>}
          {slow[token] && !(telemetry[token]?.simulation_fault_count) && <p className="rs-fault">{t("当前仿真较慢，请延长操作时间；可重置并暂停查看。")}</p>}
          <div className="rs-movable-parts" data-movable-candidate={candidate.label}>
            <strong>{t("可尝试部件")}</strong>
            <p>{t("点击按钮定位部件，再到画面中拖动。提示来自模型关节，不保证部件实际能动；卡住等现象请按实际表现判断。")}</p>
            {status === "ready" && !(movable[token]?.length) && <p>{t("未检测到独立可动部件；仍可拖动物体观察接触。")}</p>}
            <div>{(movable[token] ?? []).map((part) => <button key={part.id} type="button"
              data-movable-part={part.number} aria-pressed={highlighted[token] === part.id} disabled={status !== "ready"}
              onClick={() => {
                const id = highlighted[token] === part.id ? null : part.id;
                setHighlighted((previous) => ({ ...previous, [token]: id }));
                cores.current.get(token)?.highlightMovablePart(id);
              }}>{t("部件")} {part.number} · {part.motions.map((motion) => t(motion === "hinge" ? "旋转" : motion === "slide" ? "滑动" : "多向转动")).join(" / ")}</button>)}</div>
            {highlighted[token] != null && <small>{t("橙色为当前定位的部件；再次点击同一按钮可取消高亮。")}</small>}
          </div>
        </div>;
      })}
    </div>
    {error && <p role="alert" className="rs-error">{t(error)} <button type="button" onClick={() => setRetry((v) => v + 1)}>{t("重试")}</button></p>}
  </section>;
}
